// useChatSessions.js
import { useState, useRef, useCallback } from 'react';

const STORAGE_KEY = 'fleet_chat_sessions';
const MAX_SESSIONS = 50;
const MAX_MESSAGES_PER_SESSION = 100;

export function useChatSessions() {
  const [sessions, setSessions] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [activeSessionId, setActiveSessionId] = useState(null);
  const saveTimeoutRef = useRef(null);

  const saveSessions = useCallback((newSessions) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSessions));
    }, 300);
  }, []);

  const updateSessions = useCallback((updater) => {
    setSessions(prev => {
      let updated = typeof updater === 'function' ? updater(prev) : updater;
      
      // Enforce MAX_SESSIONS limit (keep most recently updated)
      if (updated.length > MAX_SESSIONS) {
        updated = [...updated].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_SESSIONS);
      }
      
      saveSessions(updated);
      return updated;
    });
  }, [saveSessions]);

  const createSession = useCallback((bookingCode) => {
    const newId = `session_${Date.now()}`;
    const newSession = {
      id: newId,
      title: `Booking ${bookingCode || 'Unknown'}`,
      bookingCode,
      updatedAt: Date.now(),
      messages: []
    };
    updateSessions(prev => [newSession, ...prev]);
    setActiveSessionId(newId);
    return newId;
  }, [updateSessions]);

  const updateSessionMessages = useCallback((id, messages) => {
    // Enforce MAX_MESSAGES_PER_SESSION (keep newest)
    const cappedMessages = messages.length > MAX_MESSAGES_PER_SESSION 
      ? messages.slice(messages.length - MAX_MESSAGES_PER_SESSION) 
      : messages;

    updateSessions(prev => prev.map(s => 
      s.id === id 
        ? { ...s, messages: cappedMessages, updatedAt: Date.now() } 
        : s
    ));
  }, [updateSessions]);

  const deleteSession = useCallback((id) => {
    updateSessions(prev => prev.filter(s => s.id !== id));
    if (activeSessionId === id) setActiveSessionId(null);
  }, [activeSessionId, updateSessions]);
  const bulkAddSessions = useCallback((newSessions) => {
    updateSessions(prev => {
      const merged = [...newSessions, ...prev];
      // Keep within limits, sorted by newest
      return merged.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_SESSIONS);
    });
  }, [updateSessions]);

  return {
    sessions,
    activeSessionId,
    setActiveSessionId,
    createSession,
    updateSessionMessages,
    deleteSession,
    bulkAddSessions
  };
}